import SockJS from 'sockjs-client'
import { Stomp } from '@stomp/stompjs'
import { datadogRum } from '@datadog/browser-rum-slim'

import { API_URL, getSessionToken } from '../config/config'
import widgetStorage from '../actions/storage'
import { SESSION_HEADER, sendAction } from './api'
import { actionType } from "../constants/messageActionType";

/*
 * The reason the server puts in the STOMP ERROR frame's message header when a frame is not covered by the session:
 * no credential, an expired one, or one issued for another chat.
 */
export const ACCESS_DENIED_ERROR_CODE = 'ERR_SECURITY_ACCESS_DENIED'

class WebSocketClient {
  _client
  _chatId
  _botId
  _onConnectionEstablishedStatus = []
  _onConnectionEstablishedEvents = []
  _unsentMessages = {}

  _greetingsSubscription
  _languageSubscription
  _nateraEventSubscription

  _receiveMessageCallback
  _receiveLanguageUpdateCallback
  _receiveNateraEventCallback
  _onAccessDeniedCallback

  _createNewSocketConnection() {
    this._client = Stomp.over(this._createSockJs)
    this._client.reconnect_delay = 5000
    this._client.heartbeat.incoming = 10000
    this._client.heartbeat.outgoing = 10000
    this._client.onWebSocketClose = this._onWebSocketCloseHandler
    // The credential rides the CONNECT frame rather than the handshake: SockJS cannot set handshake headers, and
    // a frame header works whether or not the browser kept the cookie. Set before every (re)connect so a
    // reconnect after getStarted carries the current token.
    this._client.beforeConnect = () => {
      const token = getSessionToken()
      this._client.connectHeaders = token ? { [SESSION_HEADER]: token } : {}
    }
    this._client.onStompError = frame => {
      if (WebSocketClient.isAccessDenied(frame)) {
        datadogRum.addAction('websocketAccessDenied', { chatId: this._chatId })
        this._onAccessDeniedCallback?.()
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    this._client.debug = () => {} //NOSONAR
  }

  _onWebSocketCloseHandler = (frame) => {
    const { code } = frame

    if (code !== 1000) { //code 1000 means - Normal Closure, see: https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
      datadogRum.addAction('websocketConnectionFailed', frame)
    }
  }

  _createSockJs = () => {
    return new SockJS(`${API_URL}/sockwidget`)
  }

  connect(botId, chatId, initiateConversation, hasPersistentMenu = false) {
    if (!this._client?.connected) {
      // A client that is not connected may still be retrying in the background; replace it, don't run two.
      this._client?.deactivate?.()
      this._createNewSocketConnection()
      // Headers deliberately empty: beforeConnect supplies them.
      this._client.connect({}, () =>
        this._onConnectionSuccess(botId, chatId, initiateConversation, hasPersistentMenu),
      )
    }
  }

  _onConnectionSuccess(botId, chatId, initiateConversation, hasPersistentMenu) {
    if (this._client.connected) {
      this.subscribeForTopics()
      this.getStarted(botId, chatId, initiateConversation)
      this.sendFailedMessages()
      hasPersistentMenu && sendAction(actionType.INITIALIZE_PERSISTENT_MENU, botId, chatId)
    }
  }

  sendFailedMessages() {
    Object.values(this._unsentMessages).forEach(message => this.sendMessage(message, this._botId, this._chatId));
    this._onConnectionEstablishedStatus.forEach(status => this.sendStatus(status, this._botId, this._chatId))
    this._onConnectionEstablishedStatus = []
    this._onConnectionEstablishedEvents.forEach(event => this.sendEvent(event, this._botId, this._chatId))
    this._onConnectionEstablishedEvents = []
  }

  configureEventHandler(receiveMessageCallback) {
    this._receiveMessageCallback = receiveMessageCallback
  }

  configureLanguageUpdateHandler(callback) {
    this._receiveLanguageUpdateCallback = callback
  }

  configureNateraEventHandler(receiveNateraEventCallback) {
    this._receiveNateraEventCallback = receiveNateraEventCallback
  }

  static isAccessDenied(frame) {
    const message = frame?.headers?.message ?? ''
    const body = typeof frame?.body === 'string' ? frame.body : ''
    return message.includes(ACCESS_DENIED_ERROR_CODE) || body.includes(ACCESS_DENIED_ERROR_CODE)
  }

  setAccessDeniedCallback(callback) {
    this._onAccessDeniedCallback = typeof callback === 'function' ? callback : null
  }

  configureChatId(chatId) {
    this._chatId = chatId
  }

  getStarted(botId, chatId, initiateConversation) {
    if (!widgetStorage.isConversationInitialized()) {
      if (initiateConversation) {
        sendAction(actionType.NATERA_START_CONVERSATION, botId, chatId)
      }
      widgetStorage.setIsConversationInitialized(true)
    }
  }

  configureBotId(botId) {
    this._botId = botId
  }

  subscribeForTopics() {
    if (this._chatId) {
      this._greetingsSubscription = this._client.subscribe(
        `/topic/messaging.${this._chatId}`,
        message => this._onReceiveMessage(message),
      )
      this._nateraEventSubscription = this._client.subscribe(
        `/topic/natera-event.${this._chatId}`,
        event => this._onReceiveNateraEvent(event),
      )
      this._languageSubscription = this._client.subscribe(
        `/topic/language-update.${this._chatId}`,
        message => this._onReceiveLanguageMessage(message),
      )
    }
  }

  _onReceiveMessage(messageInJson) {
    const message = JSON.parse(messageInJson.body)
    if (message?.message?.id) {
      delete this._unsentMessages[message.message.id]
    }
    this._receiveMessageCallback(message)
  }

  _onReceiveLanguageMessage(messageInJson) {
    const message = JSON.parse(messageInJson.body)
    this._receiveLanguageUpdateCallback(message)
  }

  _onReceiveNateraEvent(eventInJson) {
    const event = JSON.parse(eventInJson.body)
    this._receiveNateraEventCallback(event)
  }

  sendMessage(message, botId, chatId) {
    const messageCopy = JSON.parse(JSON.stringify(message))
    if (messageCopy?.message?.id) {
      this._unsentMessages[messageCopy.message.id] = messageCopy
    }
    if (this._client?.connected && chatId) {
      this._client.send('/app/widget/' + botId + '/' + chatId, {}, JSON.stringify(message))
    }
  }

  sendEvent(event) {
    if (this._client?.connected) {
      this._client.send('/app/natera/event', {}, JSON.stringify(event))
    } else {
      this._onConnectionEstablishedEvents.push(event)
    }
  }

  sendStatus(status, messageId, botId, chatId, websiteLocation) {
    const statusObj = {
      status: {
        messageId,
        status,
      },
      chatId,
      websiteLocation,
    }
    if (messageId) {
      if (this._client?.connected && messageId && chatId) {
        this._client.send('/app/widget/' + botId + '/' + chatId, {}, JSON.stringify(statusObj))
      } else {
        const statusCopy = JSON.parse(JSON.stringify(status))
        this._onConnectionEstablishedStatus.push(statusCopy)
      }
    }
  }

  disconnect() {
    if (this._client?.connected) {
      this._greetingsSubscription?.unsubscribe()
      this._languageSubscription?.unsubscribe()
      this._nateraEventSubscription?.unsubscribe()
      this._client.disconnect()
    } else {
      // Not connected may still mean reconnecting — after the server closed the socket on an ERROR frame, for
      // instance. Stop that too, or it keeps retrying with a credential that will be refused again.
      this._client?.deactivate?.()
    }
  }
}

export default WebSocketClient
